import React, { useState } from 'react';
import { Minus, Plus, Maximize2 } from 'lucide-react';
import './ZoomControl.css';

export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 1.6;
export const ZOOM_STEP = 0.05;

export const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

// Remembers a zoom level per map in this browser; 100% until changed.
export function useZoom(storageKey) {
  const [zoom, setZoomState] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(storageKey));
      return saved >= ZOOM_MIN && saved <= ZOOM_MAX ? saved : 1;
    } catch (e) { return 1; }
  });
  const setZoom = (next) => {
    const z = clampZoom(typeof next === 'function' ? next(zoom) : next);
    setZoomState(z);
    try { window.localStorage.setItem(storageKey, String(z)); } catch (e) { /* private window or blocked storage */ }
  };
  return [zoom, setZoom];
}

// A zoom slider for a canvas that can outgrow its window: minus, slider,
// plus, the percentage (click it to go back to 100%) and Fit, which sizes
// the whole drawing to the window's width.
export default function ZoomControl({ zoom, onChange, onFit, label = 'Zoom' }) {
  const pct = Math.round(zoom * 100);
  return (
    <div className="zoom-control" role="group" aria-label={label}>
      <button type="button" className="zoom-btn" onClick={() => onChange(zoom - ZOOM_STEP)} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out" title="Zoom out">
        <Minus size={14} aria-hidden="true" />
      </button>
      <input
        type="range"
        className="zoom-slider"
        min={ZOOM_MIN}
        max={ZOOM_MAX}
        step={ZOOM_STEP}
        value={zoom}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        aria-valuetext={`${pct}%`}
      />
      <button type="button" className="zoom-btn" onClick={() => onChange(zoom + ZOOM_STEP)} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in" title="Zoom in">
        <Plus size={14} aria-hidden="true" />
      </button>
      <button type="button" className="zoom-pct" onClick={() => onChange(1)} title="Back to 100%" aria-label={`${pct}%, back to 100%`}>{pct}%</button>
      {onFit && (
        <button type="button" className="zoom-btn zoom-fit" onClick={onFit} title="Fit the whole drawing to the window" aria-label="Fit to window">
          <Maximize2 size={14} aria-hidden="true" /> Fit
        </button>
      )}
    </div>
  );
}
